import { z } from 'zod'
import { TEXTOS } from '../../../../lib/glossario'
import type { MarcacaoNaTela, MarcacaoParaGravar } from '../../../../types/chamado'

/**
 * O formulário do julgamento do fiscal.
 *
 * **Quatro escolhas únicas, não seis caixas.** A tabela guarda booleanos e dois pares deles são
 * excludentes por regra do banco: aceite integral com parcial, e "corretiva" com "não corretiva".
 * Caixas independentes deixariam o fiscal marcar o par proibido e o erro voltaria como falha de
 * integridade, sem texto que ajudasse ninguém. Com escolha única o estado inválido não existe.
 *
 * **Nenhum campo tem valor inicial em chamado ainda não julgado.** Julgamento em branco não é
 * neutro: o chamado sem marcação fica fora de todos os indicadores, o que é diferente de entrar
 * como conforme. Um formulário que já viesse com "conforme, no prazo, aceito" transformaria não
 * responder em aprovar.
 */
/**
 * A mensagem em pt-BR para uma escolha única não respondida.
 *
 * **`errorMap`, e não `required_error`.** Um grupo de rádio sem nenhuma opção marcada chega ao
 * esquema como `null`, não como `undefined` — e `required_error` só vale para `undefined`. Com ele,
 * o fiscal via literalmente "Expected 'NO_PRAZO' | 'FORA_DO_PRAZO', received null" na tela. O
 * `errorMap` cobre os dois casos, além do valor fora da lista.
 *
 * @param mensagem o que dizer a quem não respondeu
 * @returns o parâmetro de erro do enum
 */
function recado(mensagem: string) {
  return { errorMap: () => ({ message: mensagem }) }
}

/** As mensagens das quatro perguntas, iguais às do servidor (`MarcarChamadoRequest`). */
const OBRIGATORIAS = {
  conformidade: 'Diga se a entrega teve inconformidade em algum artefato.',
  natureza: 'Diga que serviço foi prestado.',
  prazo: 'Diga se o atendimento cumpriu o prazo.',
  aceite: 'Diga como o usuário recebeu a entrega.',
} as const

/**
 * As quatro perguntas são obrigatórias só para o chamado que ENTRA na apuração.
 *
 * O chamado que o fiscal tira do mês (categoria errada, desistência do usuário) não é medido, e
 * exigir as quatro obrigava a inventar um julgamento — teste de uso 3, 25/09/2026. No lugar delas
 * vem o motivo, na observação: tirar um chamado de todos os denominadores é ato que a contratada
 * pode contestar.
 */
export const esquemaDeMarcacao = z
  .object({
    conformidade: z.enum(['CONFORME', 'INCONFORME'], recado(OBRIGATORIAS.conformidade)).optional(),
    natureza: z.enum(['CORRETIVA', 'NAO_CORRETIVA'], recado(OBRIGATORIAS.natureza)).optional(),
    prazo: z.enum(['NO_PRAZO', 'FORA_DO_PRAZO', 'NAO_SE_APLICA'], recado(OBRIGATORIAS.prazo)).optional(),
    aceite: z
      .enum(['INTEGRAL', 'APOS_TENTATIVAS', 'PARCIAL', 'SEM_ACEITE', 'NAO_SE_APLICA'], recado(OBRIGATORIAS.aceite))
      .optional(),
    contaNoMes: z.boolean(),
    criticidadeTis: z.enum(['', 'ALTA', 'MEDIA', 'BAIXA']),
    classificacaoServico: z.string().max(200, 'Cabe em 200 caracteres.'),
    observacao: z.string().max(4000, 'Cabe em 4000 caracteres.'),
  })
  .superRefine((valores, contexto) => {
    if (!valores.contaNoMes) {
      if (valores.observacao.trim() === '') {
        contexto.addIssue({ code: 'custom', path: ['observacao'], message: TEXTOS.marcacao.foraDaApuracao.semMotivo })
      }
      return
    }
    for (const campo of ['conformidade', 'natureza', 'prazo', 'aceite'] as const) {
      if (!valores[campo]) {
        contexto.addIssue({ code: 'custom', path: [campo], message: OBRIGATORIAS[campo] })
      }
    }
  })

export type FormularioDeMarcacao = z.infer<typeof esquemaDeMarcacao>

/**
 * O que aparece no formulário quando ele abre.
 *
 * Reedição traz o que está gravado; primeiro julgamento abre vazio — inclusive as escolhas únicas,
 * que ficam sem opção marcada até o fiscal responder.
 *
 * @param marcacao o julgamento gravado, quando há
 * @returns os valores iniciais
 */
export function deMarcacao(marcacao: MarcacaoNaTela | null): Partial<FormularioDeMarcacao> {
  if (!marcacao) {
    return { contaNoMes: true, criticidadeTis: '', classificacaoServico: '', observacao: '' }
  }
  // Linha que existe mas NÃO é julgamento: a carga de planilha grava a marcação assim que há
  // observação, e os 25 chamados de maio com "fechado em junho/2026" têm todas as colunas de
  // julgamento em branco. Preencher as quatro escolhas a partir de booleanos false inventaria
  // respostas — "fora do prazo, sem aceite" — e salvar as atribuiria ao fiscal, colocando o
  // chamado nos denominadores de ICA, IAP e IAE. A ausência de julgamento reabre o formulário.
  if (!marcacao.iapElegivel && !marcacao.tisElegivel) {
    return {
      contaNoMes: marcacao.contaNoMes,
      criticidadeTis: marcacao.criticidadeTis ?? '',
      classificacaoServico: marcacao.classificacaoServico ?? '',
      observacao: marcacao.observacao ?? '',
    }
  }
  return {
    conformidade: marcacao.icaInconformidade ? 'INCONFORME' : 'CONFORME',
    natureza: marcacao.tisElegivel ? 'CORRETIVA' : 'NAO_CORRETIVA',
    prazo: marcacao.iapNaoSeAplica ? 'NAO_SE_APLICA' : marcacao.iapNoPrazo ? 'NO_PRAZO' : 'FORA_DO_PRAZO',
    aceite: aceiteDe(marcacao),
    contaNoMes: marcacao.contaNoMes,
    criticidadeTis: marcacao.criticidadeTis ?? '',
    classificacaoServico: marcacao.classificacaoServico ?? '',
    observacao: marcacao.observacao ?? '',
  }
}

/**
 * Traduz o formulário para o corpo da requisição.
 *
 * Campo de texto vazio vira `null`, e não string vazia: no banco os dois são coisas diferentes, e
 * `''` numa coluna de observação se lê depois como "o fiscal escreveu nada" em vez de "não
 * escreveu".
 *
 * **Leva o carimbo do que estava na tela.** `marcacaoVistaEm` é o `registradoEm` da marcação que a
 * pessoa abriu; nulo quando não havia marcação. É com ele que o servidor decide entre gravar e
 * responder 409 — ver o cabeçalho de `EdicaoConcorrente` no backend.
 *
 * **Preserva o que o formulário não edita.** A gravação REESCREVE a marcação inteira, e três
 * campos vêm da carga de planilha sem ter campo na tela: a competência atribuída pelo fiscal, o
 * percentual de TIS e a advertência. Mandá-los nulos apagaria dado que ninguém pediu para apagar —
 * e a perda seria irrecuperável, porque a carga seguinte preserva a versão editada na tela em vez
 * de sobrescrevê-la. No caso da competência o efeito é grave: o chamado migra de mês e muda o
 * denominador de uma competência que pode já estar assinada.
 *
 * @param valores o formulário preenchido
 * @param atual a marcação gravada, quando é reedição
 * @returns o corpo de `PUT /chamados/{id}/marcacao`
 */
export function paraRequisicao(
  valores: FormularioDeMarcacao,
  atual: MarcacaoNaTela | null = null,
): MarcacaoParaGravar {
  const corretiva = valores.natureza === 'CORRETIVA'
  // Fora da apuração, as quatro respostas não viajam — mesmo que a pessoa as tenha marcado antes de
  // desmarcar a caixa. Gravá-las afirmaria um julgamento sobre algo que ela decidiu não medir.
  const entra = valores.contaNoMes
  return {
    conformidade: entra ? (valores.conformidade ?? null) : null,
    natureza: entra ? (valores.natureza ?? null) : null,
    prazo: entra ? (valores.prazo ?? null) : null,
    aceite: entra ? (valores.aceite ?? null) : null,
    contaNoMes: valores.contaNoMes,
    competenciaAtribuida: atual?.competenciaFiscal ?? null,
    // Criticidade é do incidente: mantê-la depois de o fiscal trocar para "outro serviço" gravaria
    // o par contraditório "não é corretiva" + "criticidade alta", com o campo já oculto na tela.
    criticidadeTis: corretiva && valores.criticidadeTis !== '' ? valores.criticidadeTis : null,
    tisPercentual: atual?.tisPercentual ?? null,
    tisAdvertencia: atual?.tisAdvertencia ?? false,
    classificacaoServico: textoOuNulo(valores.classificacaoServico),
    observacao: textoOuNulo(valores.observacao),
    // A trilha do apoio da IA sai vazia daqui e é preenchida por quem tem o parecer em mãos (o
    // formulário). Vazia por padrão porque é o caso honesto: sem parecer na tela, não houve apoio.
    parecerIaId: null,
    camposAceitosDaIa: [],
    // O carimbo do que a pessoa está vendo, para o servidor recusar salvar por cima do julgamento
    // de outra. Cenário: Ana abre o chamado às 10h00 e deixa a tela aberta; Bruno julga às 10h05;
    // Ana clica em Salvar às 10h10 com o formulário de 10h00. Sem este campo, a decisão de Bruno —
    // e o nome dele — sumiam sem deixar cópia, mexendo em ICA, IAP, IAE e TIS do mês.
    marcacaoVistaEm: atual?.registradoEm ?? null,
  }
}

/** O aceite gravado, do mais específico ao mais geral — o "após tentativas" grava também o integral. */
function aceiteDe(marcacao: MarcacaoNaTela): NonNullable<FormularioDeMarcacao['aceite']> {
  if (marcacao.iaeAceiteAposTentativas) return 'APOS_TENTATIVAS'
  if (marcacao.iaeAceiteIntegral) return 'INTEGRAL'
  if (marcacao.iaeNaoSeAplica) return 'NAO_SE_APLICA'
  return marcacao.iaeAceiteParcial ? 'PARCIAL' : 'SEM_ACEITE'
}

function textoOuNulo(valor: string): string | null {
  const limpo = valor.trim()
  return limpo === '' ? null : limpo
}
